// Popped picker horizontal scroller (spec §6.1, prototype
// `.scroller`/`.cards`/`.fade`/`.navbtn` + wireScrollers): edge fades and nav
// arrows appear only when the row actually overflows in that direction, plus a
// one-time discoverability nudge gated behind prefers-reduced-motion (A11y-4).
import type { FocusEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'cn';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';

// Tolerance so sub-pixel rounding never flips an arrow on at rest (prototype EPS).
const EPS = 4;
const NUDGE_DELAY_MS = 650;
const NUDGE_RETURN_MS = 520;
const NUDGE_DISTANCE_PX = 40;
const FOCUS_REVEAL_PADDING_PX = 2;
const STUDIO_CARD_SELECTOR = '[data-stag^="interactive:studio-picker.studio_card:"]';

export interface StudioScrollerProps {
  readonly children: ReactNode;
  /** Disable the one-time nudge (modal consumers re-mount often). */
  readonly nudge?: boolean;
}

const navChevron = (dir: 'left' | 'right'): ReactElement => (
  <svg aria-hidden='true' width='20' height='20' viewBox='0 0 16 16' fill='none'>
    <path
      d={dir === 'left' ? 'M10 4l-4 4 4 4' : 'M6 4l4 4-4 4'}
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

// Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) — reveal focused cards without scrolling ancestors.
const revealFocusedCard = (event: FocusEvent<HTMLDivElement>): void => {
  const viewport = event.currentTarget;
  const card = event.target instanceof Element ? event.target.closest(STUDIO_CARD_SELECTOR) : null;

  if (!(card instanceof HTMLElement) || !viewport.contains(card)) {
    return;
  }

  const viewportRect = viewport.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const leftDelta = cardRect.left - (viewportRect.left + FOCUS_REVEAL_PADDING_PX);
  const rightDelta = cardRect.right - (viewportRect.right - FOCUS_REVEAL_PADDING_PX);
  const delta = leftDelta < 0 ? leftDelta : rightDelta > 0 ? rightDelta : 0;

  if (delta !== 0) {
    // Focus reveal is immediate; arrow and nudge interactions retain smooth motion.
    viewport.scrollBy({ left: delta, behavior: 'instant' });
  }
};

/** Horizontal card row with overflow affordances. Presentational and pure. */
export const StudioScroller = ({ children, nudge = true }: StudioScrollerProps): ReactElement => {
  const cardsRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Recompute which arrows/fades are live from the real scroll geometry.
  const sync = useCallback((): void => {
    const el = cardsRef.current;

    if (!el) {
      return;
    }

    const max = el.scrollWidth - el.clientWidth;

    setCanLeft(el.scrollLeft > EPS);
    setCanRight(el.scrollLeft < max - EPS);
  }, []);

  useEffect(() => {
    sync();
    // Recheck after first paint and whenever late CSS/content changes geometry.
    const frame = window.requestAnimationFrame(sync);
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(sync);

    if (observer) {
      if (cardsRef.current) {
        observer.observe(cardsRef.current);
      }

      if (rowRef.current) {
        observer.observe(rowRef.current);
      }
    }

    window.addEventListener('resize', sync);

    return (): void => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [sync, children]);

  // One-time "it moves" nudge — horizontal-scroll discoverability cue.
  // Suppressed under prefers-reduced-motion (spec EC-6 / A11y-4).
  useEffect(() => {
    const el = cardsRef.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!nudge || reducedMotion || !el || el.scrollWidth - el.clientWidth <= EPS) {
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];

    timers.push(
      setTimeout(() => {
        el.scrollTo({ left: NUDGE_DISTANCE_PX, behavior: 'smooth' });
        timers.push(
          setTimeout(() => el.scrollTo({ left: 0, behavior: 'smooth' }), NUDGE_RETURN_MS)
        );
      }, NUDGE_DELAY_MS)
    );

    return (): void => timers.forEach(clearTimeout);
    // Mount-only by design: a re-run would re-nudge on every data refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = (dir: -1 | 1): void => {
    const el = cardsRef.current;

    el?.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const navButtonClass =
    'absolute top-[calc(50%_-_6px)] z-[4] flex h-40 w-40 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full p-0 ' +
    'border border-white/20 bg-base-50 text-light shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.4)] ' +
    'transition-[opacity,background-color,transform,border-color] duration-[180ms] hover:border-focus hover:bg-primary active:scale-[0.92] motion-reduce:transition-none';

  return (
    <div className='relative'>
      {/* Edge fade masks — visible only when scrollable in that direction. */}
      <span
        aria-hidden='true'
        className={twMerge(
          'pointer-events-none absolute -left-2 bottom-12 top-0 z-[2] w-[56px] bg-gradient-to-r from-dark to-transparent transition-opacity duration-[180ms]',
          canLeft ? 'opacity-100' : 'opacity-0'
        )}
      />
      <span
        aria-hidden='true'
        className={twMerge(
          'pointer-events-none absolute -right-2 bottom-12 top-0 z-[2] w-[56px] bg-gradient-to-l from-dark to-transparent transition-opacity duration-[180ms]',
          canRight ? 'opacity-100' : 'opacity-0'
        )}
      />

      {/* Nav arrows are real labelled buttons (A11y-2). */}
      {/* tabIndex -1 while hidden: an opacity-0 button must not be an
          invisible Tab stop (keyboard-reachable-interactive-elements). */}
      <button
        type='button'
        aria-label='Scroll studios left'
        aria-hidden={!canLeft}
        disabled={!canLeft}
        tabIndex={canLeft ? 0 : -1}
        {...STagNew(Sel.StudioPicker, 'scroll_left')}
        onClick={(): void => page(-1)}
        className={twMerge(
          navButtonClass,
          '-left-8',
          canLeft ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        {navChevron('left')}
      </button>
      <button
        type='button'
        aria-label='Scroll studios right'
        aria-hidden={!canRight}
        disabled={!canRight}
        tabIndex={canRight ? 0 : -1}
        {...STagNew(Sel.StudioPicker, 'scroll_right')}
        onClick={(): void => page(1)}
        className={twMerge(
          navButtonClass,
          '-right-8',
          canRight ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        {navChevron('right')}
      </button>

      <div
        ref={cardsRef}
        onScroll={sync}
        onFocus={revealFocusedCard}
        className='-mx-2 -mt-2 overflow-x-auto overflow-y-hidden scroll-smooth px-2 pb-12 pt-2 motion-reduce:scroll-auto'
        style={{ scrollbarWidth: 'thin' }}
        {...DataSTag(Sel.StudioPicker, 'scroll_viewport')}
      >
        {/* Keep centering off the overflow viewport: this inner row centers
            short sets while expanding to max-content for reachable overflow.
            Per keyboard-reachable-interactive-elements (ai.knowledge/global). */}
        <div
          ref={rowRef}
          className='flex w-max min-w-full flex-row justify-center gap-12'
          {...DataSTag(Sel.StudioPicker, 'scroll_row')}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
